<template>
  <div class="col mt-4">
    <div class="row">
      <div class="col-md-12 d-flex align-items-center">
        <div class="medium-text mb-1 font-weight-bolder">
          {{ $t('SchedulePayment') }}
        </div>
      </div>
      <template v-for="(payment, index) in schedule.paiements">
        <div class="col-md-3">
          <b-form-group :state="!paymentValidation.invalidNames.includes(index)">
            <b-form-input
              :id="`name-input-${index}`"
              v-model="payment.name"
              :class="{'is-invalid': paymentValidation.invalidNames.includes(index)}"
              :disabled="payment.locked"
              @change="validateAndEmitChanges"
              @input="validateAndEmitChanges">
            </b-form-input>
          </b-form-group>
        </div>

        <div class="col-md-2">
          <b-form-group :state="!paymentValidation.invalidDates.includes(index)"
                        label-for="datePayment-input">
            <datepicker-input
              :id="`datePayment-input-${index}`"
              v-model="payment.date"
              :is-invalid-props="paymentValidation.invalidDates.includes(index)"
              :class="{'is-invalid': paymentValidation.invalidDates.includes(index)}"
              :disabled="payment.locked"
              v-on:input="validateAndEmitChanges">
            </datepicker-input>
          </b-form-group>
        </div>

        <div class="col-md-2">
          <b-input-group :state="!paymentValidation.invalidPercentages.includes(index)"
                         label-for="percentage-input">
            <b-form-input
              :id="`percentage-input-${index}`"
              v-model="payment.percentageRounded"
              v-disable-scroll
              :class="{'is-invalid': paymentValidation.invalidPercentages.includes(index)}"
              :disabled="payment.locked"
              max="100"
              min="0"
              step="any"
              type="number"
              @change="updatePaymentAmount(index)"
              @input="updatePaymentAmount(index)">
            </b-form-input>
            <b-input-group-append :class="{'is-invalid': paymentValidation.invalidPercentages.includes(index)}">
              %
            </b-input-group-append>
          </b-input-group>
        </div>

        <div v-if="needAmountPreview" class="col-md-2">
          <b-input-group label-for="payment-amount-input">
            <b-form-input
              :id="`payment-amount-input-${index}`"
              v-model="payment.amount"
              v-disable-scroll
              :disabled="true"
              min="0"
              step="any"
              type="number"
              @change="updatePaymentPercentage(index)"
              @input="updatePaymentPercentage(index)">
            </b-form-input>
            <b-input-group-append>
              €
            </b-input-group-append>
          </b-input-group>
        </div>

        <div v-if="payment.locked" class="col-3 main-red-color mt-2">
          {{ $t("Paiement verrouillé") }}
        </div>
        <div v-else class="col-3 bold-text level-two-title mt-1">
          <trash-icon v-if="schedule.paiements && schedule.paiements.length > 1" class="cursor-pointer main-red-color"
                      size="20"
                      @click="removePayment(payment)"/>
          <MessagePlusIcon v-if="schedule.paiements && schedule.paiements.length > 0" class="cursor-pointer" size="20" @click.stop="openDescription(payment)"></MessagePlusIcon>
        </div>

        <div v-if="payment.showDescription" class="col-9 mt-2 mb-3">
          <b-form-group>
            <b-form-textarea
              :id="`description-textarea-${index}`"
              v-model="payment.description"
              rows="3"
              :placeholder="$t('Ajouter une description pour ce paiement')"
              @change="validateAndEmitChanges"
              @input="validateAndEmitChanges"
              :disabled="payment.locked">
            </b-form-textarea>
          </b-form-group>
        </div>
        <div v-if="payment.showDescription" class="col-3 mt-2 mb-3">
        </div>
      </template>

      <div v-show="paymentValidation.isTotalValid < 0" class="main-red-color col-md-4 mb-2 offset-5">
        {{ $t('Le montant total est supérieur au montant attendu : ') }}
        {{ -(paymentValidation.isTotalValid) }}%
      </div>
      <div v-show="paymentValidation.isTotalValid > 0" class="main-red-color col-md-4 mb-2 offset-5">
        {{ $t('Le montant total est inférieur au montant attendu : ') }}
        {{ paymentValidation.isTotalValid }}%
      </div>

      <div class="col-12">
        <span class="bold-text addLink" @click="addPayment">+ {{ $t('Add') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import DatepickerInput from "../../../components/b122/editFields/DatepickerInput";
import api from '../../../services/api';

export default {
  name: "ScheduleVirVefa",

  components: {
    DatepickerInput,
  },

  props: {
    initialSchedule: {
      type: Object,
      default: null
    },
    needAmountPreview: {
      type: Boolean,
      default: false
    },
    amountVente: {
      type: Number,
      default: 0
    },
    operationType: {
      type: String,
    }
  },

  data() {
    return {
      schedule: {
        id: null,
        paiements: []
      },
      paymentValidation: {
        totalPercentage: 100,
        invalidNames: [],      // Index des paiements avec nom invalide
        invalidDates: [],      // Index des paiements avec date invalide
        invalidPercentages: [], // Index des paiements avec pourcentage invalide
        isTotalValid: 0,
      },
      loading: false
    }
  },

  created() {
    this.initSchedule();
  },

  watch: {
    initialSchedule: {
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.initSchedule();
        }
      },
    },
    amountVente: {
      handler: 'recalculateAmounts',
      immediate: true
    }
  },

  methods: {
    async initSchedule() {
      if (this.initialSchedule && this.initialSchedule.paiements && this.initialSchedule.paiements.length > 0) {
        this.schedule = JSON.parse(JSON.stringify(this.initialSchedule));

        // Calculer les montants si nécessaire
        if (this.needAmountPreview && this.amountVente) {
          this.schedule.paiements.forEach(payment => {
            payment.percentageRounded = this.roundToTwo(payment.percentage || 0);
            payment.amount = this.roundToTwo(((payment.percentage || 0) / 100) * this.amountVente);
            payment.showDescription = payment.showDescription || false;
          });
        }
      } else {
        await this.fetchDefaultSchedule();
      }

      this.validatePayments();
      this.emitSchedule();
    },

    async fetchDefaultSchedule() {
      this.loading = true;
      try {
        const response = await api.get(`/operation/getDefaultScheduleVIRVEFA`);

        if (response.data && response.data.paiements && response.data.paiements.length > 0) {
          this.schedule = response.data;

          if (this.needAmountPreview && this.amountVente) {
            this.schedule.paiements.forEach(payment => {
              payment.percentageRounded = this.roundToTwo(payment.percentage || 0);
              payment.amount = this.roundToTwo(((payment.percentage || 0) / 100) * this.amountVente);
              payment.showDescription = false;
              payment.description = payment.description || '';
            });
          }
        } else {
          this.initDefaultPayment();
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du schedule par défaut', error);
        this.initDefaultPayment();
      } finally {
        this.loading = false;
      }
    },

    initDefaultPayment() {
      this.schedule = {
        id: null,
        paiements: [{
          id: null,
          name: '',
          date: null,
          percentage: 100,
          percentageRounded: 100,
          amount: this.needAmountPreview ? this.amountVente : 0,
          locked: false,
          description: '',
          showDescription: false
        }]
      };
    },

    openDescription(payment, index) {
      this.$set(payment, 'showDescription', !payment.showDescription);

      if (!payment.description) {
        this.$set(payment, 'description', '');
      }

      this.$forceUpdate();

      this.emitSchedule();
    },

    validateAndEmitChanges() {
      this.validatePayments();
      this.emitSchedule();
    },

    emitSchedule() {
      this.$emit('schedule-changed', this.schedule, this.isValid());
    },

    removePayment(paymentToRemove) {
      this.schedule.paiements = this.schedule.paiements.filter(payment => payment !== paymentToRemove);
      this.recalculatePayments(0);
      this.emitSchedule();
    },

    addPayment() {
      this.validatePayments();
      if (this.paymentValidation.isTotalValid > 0) {
        const newPayment = {
          id: null,
          name: '',
          date: null,
          percentage: this.paymentValidation.isTotalValid,
          percentageRounded: this.roundToTwo(this.paymentValidation.isTotalValid),
          amount: this.needAmountPreview ? this.roundToTwo((this.paymentValidation.isTotalValid / 100) * this.amountVente) : 0,
          locked: false,
          description: '',
          showDescription: false
        };

        if (!this.schedule.paiements) {
          this.$set(this.schedule, 'paiements', []);
        }

        this.schedule.paiements.push(newPayment);
        this.validatePayments();
        this.emitSchedule();
      } else {
        this.$bvToast.toast(this.$t('Pourcentage total déjà atteint'), {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: "warning",
          solid: true,
        });
      }
    },

    updatePaymentAmount(index) {
      const payment = this.schedule.paiements[index];
      payment.percentage = parseFloat(payment.percentageRounded) || 0;

      if (this.needAmountPreview && this.amountVente) {
        payment.amount = this.roundToTwo((payment.percentage / 100) * this.amountVente);
      }

      this.validatePayments();
      this.emitSchedule();
    },

    updatePaymentPercentage(index) {
      if (!this.needAmountPreview || !this.amountVente) return;

      const payment = this.schedule.paiements[index];
      payment.amount = parseFloat(payment.amount) || 0;

      if (this.amountVente) {
        payment.percentage = (payment.amount / this.amountVente) * 100;
        payment.percentageRounded = this.roundToTwo(payment.percentage);
      }

      this.validatePayments();
      this.emitSchedule();
    },

    recalculateAmounts() {
      if (!this.needAmountPreview || !this.amountVente) return;

      this.schedule.paiements.forEach(payment => {
        payment.amount = this.roundToTwo((payment.percentage / 100) * this.amountVente);
      });

      this.validatePayments();
      this.emitSchedule();
    },

    recalculatePayments(changedIndex) {
      const payments = this.schedule.paiements;
      let usedPercentage = 0;

      // Calculer le pourcentage déjà utilisé jusqu'à l'index changé
      for (let i = 0; i <= changedIndex; i++) {
        if (i < payments.length) {
          usedPercentage += parseFloat(payments[i].percentage) || 0;
        }
      }

      const remainingPercentage = 100 - usedPercentage;
      const remainingPayments = payments.slice(changedIndex + 1);

      if (remainingPayments.length > 0) {
        // Calculer le total des pourcentages restants
        const totalRemainingPercentage = remainingPayments.reduce(
          (sum, payment) => sum + (parseFloat(payment.percentage) || 0),
          0
        );

        // Redistribuer les pourcentages restants proportionnellement
        if (totalRemainingPercentage > 0) {
          for (let i = 0; i < remainingPayments.length - 1; i++) {
            const payment = remainingPayments[i];
            const newPercentage = (payment.percentage / totalRemainingPercentage) * remainingPercentage;
            payment.percentage = newPercentage;
            payment.percentageRounded = this.roundToTwo(newPercentage);

            if (this.needAmountPreview && this.amountVente) {
              payment.amount = this.roundToTwo((newPercentage / 100) * this.amountVente);
            }
          }
        }

        // Ajuster le dernier paiement pour atteindre exactement 100%
        const lastPayment = remainingPayments[remainingPayments.length - 1];
        const calculatedLastPercentage = remainingPercentage - remainingPayments.slice(0, -1).reduce(
          (sum, p) => sum + (parseFloat(p.percentage) || 0),
          0
        );

        lastPayment.percentage = Math.max(0, calculatedLastPercentage);
        lastPayment.percentageRounded = this.roundToTwo(lastPayment.percentage);

        if (this.needAmountPreview && this.amountVente) {
          lastPayment.amount = this.roundToTwo((lastPayment.percentage / 100) * this.amountVente);
        }
      }

      this.validatePayments();
      this.emitSchedule();
    },

    validatePayments() {
      const payments = this.schedule.paiements;
      this.paymentValidation.invalidNames = [];
      this.paymentValidation.invalidDates = [];
      this.paymentValidation.invalidPercentages = [];

      payments.forEach((payment, index) => {
        if (!payment.name || payment.name.trim() === '') {
          this.paymentValidation.invalidNames.push(index);
        }

        if (!payment.date) {
          this.paymentValidation.invalidDates.push(index);
        }

        if (!payment.percentage || payment.percentage <= 0) {
          this.paymentValidation.invalidPercentages.push(index);
        }
      });

      const totalPercentage = payments.reduce(
        (total, payment) => total + (parseFloat(payment.percentage) || 0),
        0
      );

      this.paymentValidation.isTotalValid = this.roundToTwo(100 - totalPercentage);

      const hasInvalidFields = this.paymentValidation.invalidNames.length > 0 ||
        this.paymentValidation.invalidDates.length > 0 ||
        this.paymentValidation.invalidPercentages.length > 0;

      return this.paymentValidation.isTotalValid === 0 && !hasInvalidFields;
    },

    roundToTwo(num) {
      return +(Math.round(num + "e+2") + "e-2");
    },

    isValid() {
      const hasInvalidFields = this.paymentValidation.invalidNames.length > 0 ||
        this.paymentValidation.invalidDates.length > 0 ||
        this.paymentValidation.invalidPercentages.length > 0;

      return this.paymentValidation.isTotalValid === 0 && !hasInvalidFields;
    }
  }
}
</script>

<style scoped>
.input-group-append.is-invalid {
  border-color: #dc3545 !important;
}
.is-invalid > div.input-group > input,
.is-invalid > div.input-group > div.datepicker-append {
  border-color: #dc3545 !important;
}

.is-invalid > * > * {
  border-color: #dc3545 !important;
}

.is-invalid input,
.is-invalid .datepicker-append {
  border-color: #dc3545 !important;
}
</style>



// WEBPACK FOOTER //
// src/views/operation/components/ScheduleVirVefa.vue